import { getLandedCostBreakdown } from "@redotech/redo-conversion-core-common/checkouts/checkout-shipping-options";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { FulfillmentGroup } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isCancelledIsh } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { Provider } from "@redotech/redo-order-common/provider";
import { ShopifyAddress } from "@redotech/redo-order-common/shopify-address";
import {
  DiscountCode,
  LineItem,
  Order,
  YofiScore,
} from "@redotech/redo-order-core-common/order";
import { OrderFulfillmentStatus } from "@redotech/redo-shopify-order-common/shopify-order";

export interface Transaction {
  amount: number;
  currency?: string | null;
  kind: TransactionKind;
  payment_method?: {};
  payment_details?: {
    credit_card_company?: string;
    credit_card_number?: string;
  };
  gateway?: string;
  processed_at: string;
}

export enum TransactionKind {
  SALE = "sale",
  CAPTURE = "capture",
}

export interface OrderDetailsInfo {
  orderId: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  noteFromBuyer: string;
  shopifyName: string;
  orderName: string;
  teamId: string;
  lineItems: LineItem[];
  createdAt: string;
  totalPrice: string;
  // Presentment currency of the order's money fields; drives MoneyText
  // conversion in the OMS. Absent when no source currency is reachable.
  currency?: string | null;
  externalOrderId?: string | null;
  provider?: Provider;
  fulfillmentStatus?: OrderFulfillmentStatus | null;
  billingAddress?: ShopifyAddress;
  shippingAddress?: ShopifyAddress;
  discountCodes: DiscountCode[];
  totalDiscounts: string;
  totalLineItemsPrice: string;
  totalShippingPrice: string;
  totalTax: string;
  finalSaleReturnsProtected: boolean;
  packageProtected: boolean;
  protected: boolean;
  hasExtendedWarranties: boolean;
  hasReturnableItems?: boolean;
  orderYofiScores?: Record<string, YofiScore>;
  order?: Order;
  transactions?: Transaction[];
  fulfillmentGroups?: FulfillmentGroup[];
  // Landed cost (duties, taxes, fees) paid at checkout, shop-currency major
  // units. Present only when the selected checkout option carried landed cost.
  landedCost?: { duties: number; taxes: number; fees: number };
}

export function getOrderDetailsInfoFromRedoOrder(
  order: Order & { transactions?: Transaction[] },
): OrderDetailsInfo {
  const totalShippingPrice = (order.shopify?.shipping_lines || []).reduce(
    (
      total: number,
      line: { discounted_price: string; is_removed: boolean },
    ) => {
      if (!line?.is_removed) {
        total += Number(line?.discounted_price ?? "0");
      }
      return total;
    },
    0,
  );

  const totalLineItemsPrice = parseFloat(
    order.shopify.total_line_items_price || "0",
  );
  const totalTax = parseFloat(order.shopify.total_tax || "0");
  const totalDiscounts = parseFloat(order.shopify.total_discounts || "0");
  const recalculatedTotalPrice =
    totalLineItemsPrice +
    totalShippingPrice +
    (order.shopify.taxes_included ? 0 : totalTax) -
    totalDiscounts;

  const landedCostBreakdown = getLandedCostBreakdown(
    order.selectedRedoCheckoutShippingOptions,
  );

  return {
    orderId: order._id,
    customerId: order.shopify.customer?.id,
    customerName: order.customer_name,
    customerEmail: order.shopify.customer?.email,
    noteFromBuyer: order.shopify.note,
    shopifyName: order.shopify.name,
    orderName: order.shopify.name,
    teamId: order.team,
    lineItems: order.shopify.line_items,
    createdAt: order.shopify.created_at,
    totalPrice: recalculatedTotalPrice.toFixed(2),
    currency: order.shopify.presentment_currency,
    externalOrderId: order.shopify_id,
    provider: order.provider,
    fulfillmentStatus: order.shopify.fulfillment_status,
    billingAddress: order.shopify.billing_address,
    shippingAddress: order.shopify.shipping_address,
    discountCodes: order.shopify.discount_codes,
    totalDiscounts: order.shopify.total_discounts,
    totalLineItemsPrice: order.shopify.total_line_items_price ?? "0",
    totalShippingPrice: totalShippingPrice.toFixed(2),
    totalTax: order.shopify.total_tax ?? "0",
    finalSaleReturnsProtected: order.finalSaleReturnsProtected,
    packageProtected: order.packageProtected,
    protected: order.protected,
    hasExtendedWarranties: order.extendedWarranties.length > 0,
    orderYofiScores: order.yofiScores,
    order,
    transactions: order.transactions ?? [],
    landedCost: landedCostBreakdown
      ? {
          duties: landedCostBreakdown.duties.amount,
          taxes: landedCostBreakdown.taxes.amount,
          fees: landedCostBreakdown.fees.amount,
        }
      : undefined,
  };
}

export function getOrderDetailsFromFulfillmentGroups(
  fulfillmentGroups: FulfillmentGroup[],
  externalOrderId: string,
): OrderDetailsInfo {
  // We must attempt to recreate an "order" from groups that may have been split and merged.
  // Skip groups that are voided/cancelled or are duplicates of another group (e.g. created
  // from a reship) — their line items duplicate the originals and would cause double-counting
  // when quantities are summed.
  const nonCancelledGroups = fulfillmentGroups.filter(
    (group) => !isCancelledIsh(group.status) && !group.duplicateOf,
  );
  // Non-Redo orders are rebuilt from their groups; if all are cancelled/duplicate,
  // fall back to all so the order still resolves instead of rendering blank.
  const summableGroups = nonCancelledGroups.length
    ? nonCancelledGroups
    : fulfillmentGroups;
  const relevantFulfillmentOrders = summableGroups
    .flatMap((group) => group.fulfillmentOrders)
    .filter(
      (fo) =>
        fo?.order?.id === externalOrderId ||
        fo?.order?.name === externalOrderId,
    );
  if (relevantFulfillmentOrders.length === 0) {
    throw new Error("No fulfillment group order found");
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const order = relevantFulfillmentOrders[0].order;
  const firstFulfillmentOrder = relevantFulfillmentOrders[0];
  if (!order) {
    throw new Error("No fulfillment group order found");
  }

  const fulfillmentOrderLineItems = relevantFulfillmentOrders.flatMap(
    (fo) => fo.lineItems,
  );
  const combinedLineItems = fulfillmentOrderLineItems.reduce((acc, item) => {
    const existingItem = acc.find((i) => i.externalId === item.externalId);
    if (existingItem) {
      existingItem.quantity += item.quantity;
      existingItem.totalQuantity += item.totalQuantity;
      existingItem.unfulfilledQuantity += item.unfulfilledQuantity;
    } else {
      acc.push(item);
    }
    return acc;
  }, [] as FulfillmentOrderLineItem[]);

  const lineItems: LineItem[] = combinedLineItems.map((item, index) => {
    const price = item.unitDiscountedPrice?.amount || "0";
    const quantity = item.quantity || 0;
    return {
      id: index,
      product_id: item.productId ? Number(item.productId) : null,
      sku: item.sku ?? "",
      properties:
        item.customAttributes?.map((ca) => ({
          name: ca.key,
          value: ca.value,
        })) || [],
      variant_title: item.variantTitle || "",
      variant_id: index,
      tax_lines: [],
      discount_price: price,
      reason: null,
      title: item.title || "",
      quantity,
      price,
    };
  });
  // Calculate totals from line items and fulfillment data
  const totalLineItemsPrice = combinedLineItems.reduce((sum, item) => {
    const priceString = item.unitDiscountedPrice?.amount || "0";
    const price = parseFloat(priceString) || 0;
    const quantity = item.quantity || 0;
    return sum + price * quantity;
  }, 0);

  const totalDiscountedPrice = combinedLineItems.reduce((sum, item) => {
    const priceString = item.unitDiscountedPrice?.amount || "0";
    const price = parseFloat(priceString) || 0;
    const quantity = item.quantity || 0;
    return sum + price * quantity;
  }, 0);

  const totalTax = combinedLineItems.reduce((sum, item) => {
    const taxString = item.totalTax?.amount || "0";
    const tax = parseFloat(taxString) || 0;
    const quantity = item.quantity || 0;
    return sum + tax * quantity;
  }, 0);

  const totalShippingPrice = parseFloat(order.shippingCost.amount || "0");
  const totalDiscounts = totalLineItemsPrice - totalDiscountedPrice;
  const totalPrice =
    totalLineItemsPrice + totalShippingPrice + totalTax - totalDiscounts;

  // All of this order's money is rated in one presentment currency; the
  // shipping cost (and each line item's unit price) carry it.
  const currency =
    order.shippingCost.currency ??
    combinedLineItems[0]?.unitDiscountedPrice?.currency;

  const landedCostBreakdown = getLandedCostBreakdown(
    relevantFulfillmentOrders.flatMap(
      (fo) => fo?.deliveryMethod?.selectedRedoCheckoutShippingOptions ?? [],
    ),
  );

  return {
    orderId: order.id,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    teamId: fulfillmentGroups[0].team,
    lineItems,
    createdAt: order.createdAt.toISOString(),
    totalPrice: totalPrice.toFixed(2),
    currency,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    provider: firstFulfillmentOrder.provider,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    customerId: firstFulfillmentOrder.customer?.id,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    customerName: firstFulfillmentOrder.customer?.name,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    customerEmail: firstFulfillmentOrder.customer?.email ?? undefined,
    noteFromBuyer: "",
    shopifyName: "",
    externalOrderId,
    orderName: order.name,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    billingAddress: firstFulfillmentOrder.billingAddress
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fulfillmentAddressToShopifyAddress(firstFulfillmentOrder.billingAddress)
      : undefined,
    shippingAddress: firstFulfillmentOrder?.destinationAddress
      ? fulfillmentAddressToShopifyAddress(
          firstFulfillmentOrder?.destinationAddress,
        )
      : undefined,
    discountCodes: [],
    totalDiscounts: totalDiscounts.toFixed(2),
    totalLineItemsPrice: totalLineItemsPrice.toFixed(2),
    totalShippingPrice: totalShippingPrice.toFixed(2),
    totalTax: totalTax.toFixed(2),
    finalSaleReturnsProtected: false,
    packageProtected: order.packageProtected ?? false,
    protected: false,
    hasExtendedWarranties: false,
    fulfillmentGroups,
    landedCost: landedCostBreakdown
      ? {
          duties: landedCostBreakdown.duties.amount,
          taxes: landedCostBreakdown.taxes.amount,
          fees: landedCostBreakdown.fees.amount,
        }
      : undefined,
  };
}

function fulfillmentAddressToShopifyAddress(
  address: FulfillmentOrderAddress,
): ShopifyAddress {
  return {
    name: address.name,
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    country: address.countryCode,
    country_code: address.countryCode,
    province: address.province,
    zip: address.zip,
    phone: address.phone,
    company: address.company,
  };
}
